<template>
    <div class="voice-selector">
        <!-- Tab kontrolü -->
        <a-tabs default-active-key="eleven" @change="handleTabChange">
            <a-tab-pane key="eleven" tab="ElevenLabs">
                <div class="voice-list-container">
                    <div class="loading-container" v-if="loading">
                        <a-spin tip="Sesler yükleniyor..." />
                    </div>
                    <div class="voice-list" v-else>
                        <!-- Add Custom Voice Butonu -->
                        <div class="add-custom-voice-button-wrapper">
                            <a-button
                                type="dashed"
                                icon="plus"
                                @click="$feature.beforeCreateVoiceClone(showAddCustomVoiceModal, getCustomVoiceCount())"
                                class="add-custom-voice-button"
                            >
                                Özel Ses Ekle
                            </a-button>
                        </div>

                        <a-table
                            :columns="columns"
                            :data-source="getFilteredVoices('elevenlabs')"
                            :pagination="false"
                            :scroll="{ y: 400 }"
                            :row-key="(record) => record.voice_id"
                        >
                            <!-- Ses (Avatar ve İsim) -->
                            <template slot="voice" slot-scope="text, record">
                                <div class="voice-cell">
                                    <a-avatar class="voice-avatar" shape="square"
                                        >{{ getInitials(record.voice_name) }}
                                    </a-avatar>
                                    <span class="voice-name">{{ record.voice_name }}</span>
                                </div>
                            </template>

                            <!-- Özellikler (accent, gender, age) -->
                            <template slot="traits" slot-scope="text, record">
                                <div class="traits-cell">
                                    <a-tag v-if="record.accent">{{ record.accent }}</a-tag>
                                    <a-tag v-if="record.gender">{{ record.gender }}</a-tag>
                                    <a-tag v-if="record.age">{{ record.age }}</a-tag>
                                </div>
                            </template>

                            <!-- Voice ID -->
                            <template slot="voiceId" slot-scope="text, record">
                                <div class="voice-id-cell">
                                    {{ record.voice_id }}
                                </div>
                            </template>

                            <!-- İşlemler (Seç ve Dinle) -->
                            <template slot="options" slot-scope="text, record">
                                <div class="options-cell">
                                    <a-button
                                        type="primary"
                                        size="small"
                                        @click="selectVoice(record)"
                                        :disabled="isCurrentlySelected(record)"
                                    >
                                        {{ isCurrentlySelected(record) ? "Seçili" : "Seç" }}
                                    </a-button>
                                    <a-button
                                        type="default"
                                        size="small"
                                        @click="previewVoice(record)"
                                        :disabled="!record.preview_audio_url"
                                    >
                                        <i class="material-icons">volume_up</i>
                                    </a-button>
                                </div>
                            </template>
                        </a-table>
                    </div>
                </div>
            </a-tab-pane>
            <a-tab-pane key="openai" tab="OpenAI">
                <div class="voice-list-container">
                    <div class="loading-container" v-if="loading">
                        <a-spin tip="Sesler yükleniyor..." />
                    </div>
                    <div class="voice-list" v-else>
                        <a-table
                            :columns="columns"
                            :data-source="getFilteredVoices('openai')"
                            :pagination="false"
                            :scroll="{ y: 400 }"
                            :row-key="(record) => record.voice_id"
                        >
                            <!-- Ses (Avatar ve İsim) -->
                            <template slot="voice" slot-scope="text, record">
                                <div class="voice-cell">
                                    <a-avatar class="voice-avatar" shape="square"
                                        >{{ getInitials(record.voice_name) }}
                                    </a-avatar>
                                    <span class="voice-name">{{ record.voice_name }}</span>
                                </div>
                            </template>

                            <!-- Özellikler (accent, gender, age) -->
                            <template slot="traits" slot-scope="text, record">
                                <div class="traits-cell">
                                    <a-tag v-if="record.accent">{{ record.accent }}</a-tag>
                                    <a-tag v-if="record.gender">{{ record.gender }}</a-tag>
                                    <a-tag v-if="record.age">{{ record.age }}</a-tag>
                                </div>
                            </template>

                            <!-- Voice ID -->
                            <template slot="voiceId" slot-scope="text, record">
                                <div class="voice-id-cell">
                                    {{ record.voice_id }}
                                </div>
                            </template>

                            <!-- İşlemler (Seç ve Dinle) -->
                            <template slot="options" slot-scope="text, record">
                                <div class="options-cell">
                                    <a-button
                                        type="primary"
                                        size="small"
                                        @click="selectVoice(record)"
                                        :disabled="isCurrentlySelected(record)"
                                    >
                                        {{ isCurrentlySelected(record) ? "Seçili" : "Seç" }}
                                    </a-button>
                                    <a-button
                                        type="default"
                                        size="small"
                                        @click="previewVoice(record)"
                                        :disabled="!record.preview_audio_url"
                                    >
                                        <i class="material-icons">volume_up</i>
                                    </a-button>
                                </div>
                            </template>
                        </a-table>
                    </div>
                </div>
            </a-tab-pane>
        </a-tabs>

        <!-- Ses önizleme için audio oynatıcı -->
        <audio ref="audioPlayer" style="display: none"></audio>

        <!-- Özel Ses Ekleme Modalı -->
        <modal2
            v-if="isAddCustomVoiceModalVisible"
            title="Add Custom Voice"
            @cancel="closeAddCustomVoiceModal"
            class="custom-voice-modal"
            :showFooter="false"
        >
            <div class="custom-voice-modal">
                <!-- Tab kontrolü - Ses Klonlama ve Topluluk Sesleri -->
                <a-tabs v-model="customVoiceTab">
                    <a-tab-pane key="voice-clone" tab="Voice Clone">
                        <div class="voice-clone-container">
                            <h3>Ses Klonlama</h3>
                            <p>30 saniyelik bir ses kaydı yükleyerek kendi sesinizi klonlayabilirsiniz.</p>

                            <div class="voice-name-input">
                                <label>Voice Name</label>
                                <a-input v-model="voiceCloneData.voiceName" placeholder="Enter a voice name" />
                            </div>

                            <div class="upload-container">
                                <div
                                    class="upload-dropzone"
                                    @dragover.prevent
                                    @drop.prevent="onFileDrop"
                                    @click="triggerFileInput"
                                >
                                    <i class="material-icons upload-icon">cloud_upload</i>
                                    <div class="upload-text">Choose a file or drag & drop it here.</div>
                                    <div class="upload-details">Audio and video formats, up to 10 MB.</div>
                                    <input
                                        ref="fileInput"
                                        type="file"
                                        style="display: none"
                                        accept="audio/*,video/*"
                                        @change="onFileChange"
                                    />
                                </div>

                                <div class="record-option">
                                    <a-button type="default" @click="toggleRecording" :danger="isRecording">
                                        <i class="material-icons">{{ isRecording ? "stop" : "mic" }}</i>
                                        {{ isRecording ? "Kaydı Durdur" : "Mikrofondan Kaydet" }}
                                    </a-button>
                                    <span v-if="recordingTime > 0" class="recording-timer">
                                        {{ formatRecordingTime(recordingTime) }}
                                    </span>
                                </div>

                                <div v-if="recordedAudio" class="recorded-audio">
                                    <div class="file-info">
                                        <i class="material-icons">audio_file</i>
                                        <span>Mikrofon Kaydı ({{ formatRecordingTime(recordingTime) }})</span>
                                    </div>
                                    <div class="audio-controls">
                                        <a-button type="link" @click="playRecordedAudio">
                                            <i class="material-icons">play_arrow</i>
                                        </a-button>
                                        <a-button type="link" @click="removeRecordedAudio">
                                            <i class="material-icons">close</i>
                                        </a-button>
                                    </div>
                                </div>

                                <div
                                    v-if="voiceCloneData.audioFile && !voiceCloneData._isFromRecording"
                                    class="selected-file"
                                >
                                    <div class="file-info">
                                        <i class="material-icons">audio_file</i>
                                        <span>{{ voiceCloneData.audioFile.name }}</span>
                                    </div>
                                    <a-button type="link" @click="removeSelectedFile">
                                        <i class="material-icons">close</i>
                                    </a-button>
                                </div>
                            </div>

                            <div class="terms-checkbox">
                                <a-checkbox v-model="voiceCloneData.termsAccepted">
                                    Yüklediğim ve klonladığım ses örnekleri için gerekli tüm haklara veya izinlere sahip
                                    olduğumu, platform tarafından oluşturulan içeriği herhangi bir yasa dışı,
                                    dolandırıcılık veya zararlı amaçla kullanmayacağımı onaylıyorum. Ünlülerin, devlet
                                    yetkililerinin veya izinsiz üçüncü şahısların seslerini klonlamanın suç teşkil
                                    edebileceğini anlıyor ve kabul ediyorum.
                                </a-checkbox>
                            </div>

                            <div class="actions">
                                <a-button @click="closeAddCustomVoiceModal">Cancel</a-button>
                                <a-button
                                    type="primary"
                                    @click="submitVoiceClone"
                                    :disabled="!canSubmitVoiceClone"
                                    :loading="isSubmitting"
                                >
                                    Save
                                </a-button>
                            </div>
                        </div>
                    </a-tab-pane>
                    <a-tab-pane key="community-voices" tab="Community Voices">
                        <div class="community-voices-container">
                            <h3>Topluluk Seslerini Ara</h3>
                            <p>Topluluk tarafından oluşturulmuş sesleri arayıp kullanabilirsiniz.</p>

                            <div class="search-container">
                                <a-input-search
                                    v-model="communitySearchQuery"
                                    placeholder="Ara..."
                                    enter-button
                                    @search="searchCommunityVoices"
                                />
                            </div>

                            <div v-if="isSearching" class="loading-container">
                                <a-spin tip="Aranıyor..." />
                            </div>

                            <div v-else-if="communityVoices.length > 0" class="community-voices-results">
                                <a-list item-layout="horizontal" :data-source="communityVoices">
                                    <a-list-item slot="renderItem" slot-scope="voice" :key="voice.voice_id">
                                        <a-list-item-meta :description="getCommunityVoiceDescription(voice)">
                                            <span slot="title"
                                                >{{ voice.name }} <a-tag>{{ voice.accent }}</a-tag></span
                                            >
                                        </a-list-item-meta>
                                        <div class="list-item-actions">
                                            <a-button
                                                type="default"
                                                size="small"
                                                @click="previewCommunityVoice(voice)"
                                                :disabled="!voice.preview_url"
                                            >
                                                <i class="material-icons">volume_up</i>
                                            </a-button>
                                            <a-button type="primary" size="small" @click="selectCommunityVoice(voice)">
                                                Seç
                                            </a-button>
                                        </div>
                                    </a-list-item>
                                </a-list>
                            </div>

                            <div v-else-if="hasSearched" class="no-results">
                                <p>Aramanıza uygun ses bulunamadı. Lütfen başka bir arama terimi deneyin.</p>
                            </div>

                            <div class="actions">
                                <a-button @click="closeAddCustomVoiceModal">İptal</a-button>
                            </div>
                        </div>
                    </a-tab-pane>
                </a-tabs>
            </div>
        </modal2>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import _ from "lodash";
import { VOICE_AGENT_API } from "../../../../../api/voiceAgent";
import Modal2 from "@/components/common/Modal2.vue";

export default {
    name: "VoiceSelector",
    props: {
        selectedVoice: {
            type: Object,
            default: null,
        },
    },
    components: {
        Modal2
    },
    data() {
        return {
            loading: false,
            voices: [],
            activeTab: "eleven",
            columns: [
                {
                    title: "Voice",
                    dataIndex: "voice_name",
                    key: "voice",
                    scopedSlots: { customRender: "voice" },
                    width: "25%",
                },
                {
                    title: "Trait",
                    key: "traits",
                    scopedSlots: { customRender: "traits" },
                    width: "25%",
                },
                {
                    title: "Voice ID",
                    dataIndex: "voice_id",
                    key: "voiceId",
                    scopedSlots: { customRender: "voiceId" },
                    width: "25%",
                },
                {
                    title: "Options",
                    key: "options",
                    scopedSlots: { customRender: "options" },
                    width: "25%",
                },
            ],
            // Özel Ses Ekleme Modal State
            isAddCustomVoiceModalVisible: false,
            customVoiceTab: "voice-clone",
            voiceCloneData: {
                voiceName: "",
                audioFile: null,
                termsAccepted: false,
                _isFromRecording: false,
            },
            isSubmitting: false,
            // Mikrofon Kayıt Durumu
            isRecording: false,
            mediaRecorder: null,
            recordedChunks: [],
            recordedAudio: null,
            recordingTime: 0,
            recordingTimer: null,
            // Topluluk Sesleri Arama
            communitySearchQuery: "",
            communityVoices: [],
            isSearching: false,
            hasSearched: false,
        };
    },
    computed: {
        canSubmitVoiceClone() {
            return (
                this.voiceCloneData.voiceName.trim() !== "" &&
                (this.voiceCloneData.audioFile !== null || this.recordedAudio !== null) &&
                this.voiceCloneData.termsAccepted
            );
        },
    },
    mounted() {
        this.fetchVoices();
    },
    methods: {
        ...mapActions({
            listVoices: "voiceAgent/listVoices",
        }),
        async fetchVoices() {
            this.loading = true;
            try {
                const result = await this.listVoices();
                this.voices = result || [];
            } catch (error) {
                console.error("Ses listesi yüklenirken hata oluştu:", error);
                this.$message.error("Ses listesi yüklenemedi!");
            } finally {
                this.loading = false;
            }
        },
        handleTabChange(activeKey) {
            this.activeTab = activeKey;
        },
        getFilteredVoices(provider) {
            if (!this.voices || this.voices.length === 0) return [];

            return this.voices.filter((voice) => {
                if (provider === "elevenlabs") {
                    return voice.provider === "elevenlabs";
                } else if (provider === "openai") {
                    return voice.provider === "openai";
                }
                return false;
            });
        },
        getCustomVoiceCount() {
            return this.voices.filter((voice) => voice.voice_type === "custom").length;
        },
        getInitials(name) {
            if (!name) return "?";
            return name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase();
        },
        selectVoice(voice) {
            this.$emit("select", voice);
        },
        previewVoice(voice) {
            if (voice.preview_audio_url) {
                const audioPlayer = this.$refs.audioPlayer;
                audioPlayer.src = voice.preview_audio_url;
                audioPlayer.play();
            }
        },
        isCurrentlySelected(voice) {
            if (!this.selectedVoice) return false;
            return this.selectedVoice.voice_id === voice.voice_id;
        },

        // Özel Ses Ekleme Fonksiyonları
        showAddCustomVoiceModal() {
            this.isAddCustomVoiceModalVisible = true;
        },
        closeAddCustomVoiceModal() {
            this.isAddCustomVoiceModalVisible = false;
            // Form verilerini sıfırla
            this.voiceCloneData = {
                voiceName: "",
                audioFile: null,
                termsAccepted: false,
                _isFromRecording: false,
            };
            this.communitySearchQuery = "";
            this.communityVoices = [];
            this.hasSearched = false;

            // Mikrofon kaydı ile ilgili verileri de temizle
            this.removeRecordedAudio();
            if (this.isRecording) {
                this.stopRecording();
            }
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 10 * 1024 * 1024) {
                    // 10MB
                    this.$message.error("Dosya boyutu 10MB'dan küçük olmalıdır.");
                    return;
                }
                // Kullanıcı dosya seçtiğinde, kaydedilmiş sesi temizle
                this.removeRecordedAudio();
                this.voiceCloneData.audioFile = file;
                this.voiceCloneData._isFromRecording = false;
            }
        },
        onFileDrop(event) {
            const file = event.dataTransfer.files[0];
            if (file) {
                if (file.size > 10 * 1024 * 1024) {
                    // 10MB
                    this.$message.error("Dosya boyutu 10MB'dan küçük olmalıdır.");
                    return;
                }
                this.voiceCloneData.audioFile = file;
            }
        },
        removeSelectedFile() {
            this.voiceCloneData.audioFile = null;
            this.voiceCloneData._isFromRecording = false;
            this.$refs.fileInput.value = "";
        },
        // Mikrofon kayıt işlemleri
        async toggleRecording() {
            if (this.isRecording) {
                this.stopRecording();
            } else {
                await this.startRecording();
            }
        },
        async startRecording() {
            try {
                // Mevcut ses dosyasını temizle
                this.removeRecordedAudio();
                this.removeSelectedFile();

                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.mediaRecorder = new MediaRecorder(stream);
                this.recordedChunks = [];

                this.mediaRecorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        this.recordedChunks.push(e.data);
                    }
                };

                this.mediaRecorder.onstop = () => {
                    const audioBlob = new Blob(this.recordedChunks, { type: "audio/webm" });
                    this.recordedAudio = audioBlob;

                    // Kaydedilen sesi voiceCloneData'ya ekle, ama UI'da gösterme
                    const fileName = `recording_${new Date().toISOString()}.webm`;
                    // Dosyayı gizli olarak tanımla, UI'da gösterme
                    this.voiceCloneData.audioFile = new File([audioBlob], fileName, { type: "audio/webm" });
                    this.voiceCloneData._isFromRecording = true;

                    clearInterval(this.recordingTimer);
                };

                this.mediaRecorder.start();
                this.isRecording = true;
                this.recordingTime = 0;
                this.recordingTimer = setInterval(() => {
                    this.recordingTime++;

                    // 30 saniye kayıt limiti
                    if (this.recordingTime >= 30) {
                        this.stopRecording();
                    }
                }, 1000);
            } catch (error) {
                console.error("Mikrofon erişim hatası:", error);
                this.$message.error("Mikrofona erişilemedi. Lütfen izinleri kontrol edin.");
            }
        },
        stopRecording() {
            if (this.mediaRecorder && this.isRecording) {
                this.mediaRecorder.stop();
                this.isRecording = false;

                // Kaydedilen ses akışını durdurmak için tüm parçaları kapatıyoruz
                this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
            }
        },
        playRecordedAudio() {
            if (this.recordedAudio) {
                const audioUrl = URL.createObjectURL(this.recordedAudio);
                const audioPlayer = this.$refs.audioPlayer;
                audioPlayer.src = audioUrl;
                audioPlayer.play();
            }
        },
        removeRecordedAudio() {
            this.recordedAudio = null;
            this.recordingTime = 0;
            if (this.isRecording) {
                this.stopRecording();
            }

            // Eğer bu kayıttan gelen bir dosya varsa, onu da temizle
            if (this.voiceCloneData._isFromRecording) {
                this.voiceCloneData.audioFile = null;
                this.voiceCloneData._isFromRecording = false;
            }
        },
        formatRecordingTime(seconds) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
        },
        async submitVoiceClone() {
            try {
                this.isSubmitting = true;
                // API call için FormData oluştur
                const formData = new FormData();
                formData.append("voice_name", this.voiceCloneData.voiceName);
                formData.append("files", this.voiceCloneData.audioFile);

                // Doğrudan API çağrısı
                const result = await VOICE_AGENT_API.voices.clone(formData);

                console.log("Ses klonlama sonucu:", result);
                this.$message.success(`"${result.voice_name}" sesi başarıyla klonlandı!`);

                // API'den dönen sonucu doğrudan üst bileşene gönderiyoruz
                this.$emit("select", result);

                this.closeAddCustomVoiceModal();
            } catch (error) {
                console.error("Ses klonlama hatası:", error);
                this.$message.error("Ses klonlanırken bir hata oluştu!");
            } finally {
                this.isSubmitting = false;
            }
        },

        // Topluluk Sesleri Fonksiyonları
        async searchCommunityVoices() {
            if (!this.communitySearchQuery.trim()) {
                this.$message.warning("Lütfen bir arama terimi girin.");
                return;
            }

            try {
                this.isSearching = true;
                this.hasSearched = true;

                // API çağrısı
                const result = await this.$store.dispatch(
                    "voiceAgent/searchCommunityVoices",
                    this.communitySearchQuery
                );
                this.communityVoices = result;
            } catch (error) {
                console.error("Topluluk sesleri arama hatası:", error);
                const msg = (error.response && error.response.data && error.response.data.error) || error.message || "Topluluk sesleri aranırken bir hata oluştu";
                this.$message.error(msg);
            } finally {
                this.isSearching = false;
            }
        },
        getCommunityVoiceDescription(voice) {
            return voice.description || `${voice.gender || ""} ${voice.accent || ""} voice`;
        },
        previewCommunityVoice(voice) {
            if (voice.preview_url) {
                const audioPlayer = this.$refs.audioPlayer;
                audioPlayer.src = voice.preview_url;
                audioPlayer.play();
            }
        },
        async selectCommunityVoice(voice) {
            try {
                this.isSubmitting = true;

                // Önce topluluk sesini sistemimize ekliyoruz
                console.log(`Topluluk sesi ${voice.voice_id} ekleniyor...`);

                const publicUserId = voice.public_owner_id;

                // Doğrudan API çağrısı yapıyoruz
                const result = await VOICE_AGENT_API.voices.addCommunity(voice.voice_id, publicUserId, voice.name);

                console.log("Topluluk sesi ekleme sonucu:", result);

                // API'den dönen sonucu doğrudan kullan
                this.$message.success(`"${result.voice_name}" sesi başarıyla eklendi!`);

                // API'den gelen sonucu doğrudan emit et
                this.$emit("select", result);
                this.closeAddCustomVoiceModal();
            } catch (error) {
                console.error("Topluluk sesi ekleme hatası:", error);
                this.$message.error("Topluluk sesi eklenirken bir hata oluştu!");
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.voice-selector {
    .voice-list-container {
        padding: 15px;
        position: relative;

        .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 300px;
        }

        .add-custom-voice-button-wrapper {
            margin-bottom: 15px;

            .add-custom-voice-button {
                display: flex;
                align-items: center;
                gap: 5px;

                .material-icons {
                    font-size: 16px;
                }
            }
        }

        .voice-list {
            :deep(.ant-table-tbody > tr > td) {
                padding: 10px 16px;
            }

            .voice-cell {
                display: flex;
                align-items: center;

                .voice-avatar {
                    margin-right: 10px;
                    background-color: #1890ff;
                }

                .voice-name {
                    font-weight: 500;
                }
            }

            .traits-cell {
                display: flex;
                flex-wrap: wrap;
                gap: 5px;
            }

            .voice-id-cell {
                font-family: monospace;
                color: #595959;
                font-size: 12px;
            }

            .options-cell {
                display: flex;
                gap: 8px;

                button {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }
    }
}
</style>
<style lang="scss">
// Özel Ses Ekleme Modal Stilleri
.custom-voice-modal {
    .ant-modal {
        width: 720px !important;
    }

    .voice-clone-container,
    .community-voices-container {
        padding: 10px 0;

        h3 {
            margin-bottom: 10px;
        }

        p {
            margin-bottom: 20px;
            color: #606060;
        }
    }

    .voice-name-input {
        margin-bottom: 20px;

        label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
        }
    }

    .upload-container {
        margin-bottom: 20px;

        .upload-dropzone {
            border: 2px dashed #d9d9d9;
            border-radius: 4px;
            padding: 30px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
                border-color: #1890ff;
            }

            .upload-icon {
                font-size: 36px;
                color: #d9d9d9;
                margin-bottom: 10px;
            }

            .upload-text {
                font-size: 16px;
                margin-bottom: 5px;
            }

            .upload-details {
                font-size: 12px;
                color: #999;
            }
        }

        .record-option {
            margin-top: 10px;
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px;
            background-color: #f5f5f5;
            border-radius: 4px;

            .recording-timer {
                font-family: monospace;
                font-weight: bold;
                color: #f5222d;
            }
        }

        .recorded-audio {
            margin-top: 10px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px;
            background-color: #f5f5f5;
            border-radius: 4px;

            .file-info {
                display: flex;
                align-items: center;
                gap: 5px;

                .material-icons {
                    color: #1890ff;
                }
            }

            .audio-controls {
                display: flex;
                gap: 8px;
            }
        }

        .selected-file {
            margin-top: 10px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px;
            background-color: #f5f5f5;
            border-radius: 4px;

            .file-info {
                display: flex;
                align-items: center;
                gap: 5px;

                .material-icons {
                    color: #1890ff;
                }
            }
        }
    }

    .terms-checkbox {
        margin-bottom: 20px;
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;
    }

    // Topluluk Sesleri
    .search-container {
        margin-bottom: 20px;
    }

    .community-voices-results {
        max-height: 300px;
        overflow-y: auto;
        margin-bottom: 20px;
        border: 1px solid #f0f0f0;
        border-radius: 4px;

        .list-item-actions {
            display: flex;
            gap: 8px;
        }
    }

    .no-results {
        text-align: center;
        padding: 30px 0;
        color: #999;
    }
}
</style>
